import { Box, Typography } from '@mui/material';
import { useEffect, useRef, useState, type ChangeEvent, type ReactElement } from 'react';

import useDebounce from 'app/hooks/useDebounce';

import { PreviewIframe } from '../../components/PreviewIframe';

export interface BrandPalette {
  primary: string;
}

interface BrandPaletteEditorProps {
  initialColor?: string;
  websiteId?: string;
  onChange?: (palette: BrandPalette) => void;
}

export function BrandPaletteEditor({
  initialColor = '',
  websiteId,
  onChange,
}: BrandPaletteEditorProps): ReactElement {
  const [primaryColor, setPrimaryColor] = useState(initialColor);
  const [debouncedColor, setDebouncedColor] = useState(initialColor);
  const debouncedSetColor = useDebounce(setDebouncedColor, 150);

  const hasUserPicked = useRef(false);

  const handleColorChange = (e: ChangeEvent<HTMLInputElement>) => {
    hasUserPicked.current = true;
    setPrimaryColor(e.target.value);
    debouncedSetColor(e.target.value);
  };

  // `initialColor` can land after mount — onboarding presets it from the organisation's
  // branding, and the edit page from the fetched theme. Adopt it until the user picks a
  // colour themselves; from then on their choice stands, however late the prop settles.
  useEffect(() => {
    if (hasUserPicked.current || !initialColor) return;
    setPrimaryColor(initialColor);
    setDebouncedColor(initialColor);
  }, [initialColor]);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (primaryColor) {
      onChangeRef.current?.({ primary: primaryColor });
    }
  }, [primaryColor]);

  return (
    <>
      <Box className="vtwo:flex vtwo:flex-col vtwo:md:flex-row vtwo:gap-8">
        <Box className="vtwo:flex-1">
          <Typography className="vtwo:font-bold vtwo:text-typography-primary vtwo:mb-2">
            How it works:
          </Typography>
          <Typography className="vtwo:text-typography-primary">
            One colour, a full palette. Pick your brand colour and we take it from there. Your
            chosen colour is used for buttons and interactive elements, while we create lighter and
            darker variations to style backgrounds and secondary details automatically.
          </Typography>
        </Box>

        <Box className="vtwo:flex-1">
          <Typography className="vtwo:font-bold vtwo:text-typography-primary vtwo:mb-2">
            Primary color
          </Typography>
          <Box
            component="label"
            className="vtwo:flex vtwo:cursor-pointer vtwo:items-center vtwo:justify-between vtwo:rounded-lg vtwo:border vtwo:border-[#D1D5DB] vtwo:bg-white vtwo:px-4 vtwo:py-3"
          >
            <Typography className="vtwo:text-typography-primary">
              {primaryColor || 'Select color'}
            </Typography>
            <Box className="vtwo:relative vtwo:size-5">
              <Box
                className="vtwo:size-5 vtwo:rounded-full vtwo:border vtwo:border-black/10"
                style={{ backgroundColor: primaryColor || '#ccc' }}
              />
              <input
                type="color"
                value={(primaryColor || '#000000').toLowerCase()}
                onChange={handleColorChange}
                aria-label="Primary color"
                className="vtwo:absolute vtwo:inset-0 vtwo:size-full vtwo:cursor-pointer vtwo:opacity-0"
              />
            </Box>
          </Box>
        </Box>
      </Box>

      {debouncedColor && websiteId && (
        <>
          <Box className="vtwo:my-8 vtwo:border-t vtwo:border-gray-200" />
          <Typography className="vtwo:text-typography-primary vtwo:mb-4">
            This is how your key elements will look:
          </Typography>
          <Box
            className="vtwo:rounded-lg vtwo:border vtwo:border-gray-200 vtwo:overflow-hidden"
            style={{ height: 320 }}
          >
            <PreviewIframe websiteId={websiteId} scaled={false} colorPreview={debouncedColor} />
          </Box>
        </>
      )}
    </>
  );
}
